import { useState, useEffect, useRef } from 'react';
import { fetchProducts } from '../api/fetchProducts';

const ENDPOINT = {
  panel: 'panel_models',
  inwerter: 'inverter_models',
  magazyn: 'magazine_models',
  wallbox: 'wallbox_models',
};

// Parametry elektryczne magazynów (spec_magazines). Ta sama lista jest w
// useProducts.js — filtr działający w widoku modeli i milczący w widoku ofert
// to błąd, który przy panelach już raz trzeba było naprawiać.
export const MAGAZINE_ELEC_PARAMS = [
  'min_usable_energy', 'max_usable_energy',
  'min_capacity_ah', 'max_capacity_ah',
  'min_voltage_nominal', 'max_voltage_nominal',
  'min_charge_current', 'max_charge_current',
  'min_discharge_current', 'max_discharge_current',
  'min_charge_power', 'max_charge_power',
  'min_discharge_power', 'max_discharge_power',
  'min_peak_power', 'min_cycle_life', 'min_dod',
  'min_energy_throughput', 'min_parallel_units',
];

// Generyczny hook trybu "Grupuj oferty" dla wszystkich typów produktów.
// enabled=false -> nie pobiera (żeby nie dublować fetchy z useProducts).
export const useProductModels = (productType, pageIndex, pageSize, filters, sortOrder, search, enabled = true) => {
  const [models, setModels] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Guard "latest-wins" — patrz useProducts.js (starsza odpowiedź nie nadpisuje świeższej).
  const latestReq = useRef(0);

  const fetchModels = async () => {
    const reqId = ++latestReq.current;
    setIsLoading(true);
    const offset = (pageIndex - 1) * pageSize;
    const q = new URLSearchParams({ limit: pageSize, offset });

    if (search) q.append('search', search);

    if (sortOrder === 'availability') q.append('order_by', 'availability');
    else if (sortOrder === 'offers') q.append('order_by', 'offers');
    else if (sortOrder) q.append('order_by_price', sortOrder);

    if (filters.available_only) q.append('available_only', 'true');
    // „Tylko jawne ceny" — na liście modeli chowa rodziny złożone wyłącznie
    // z ofert B2B (cena po zalogowaniu), czyli karty bez żadnej realnej ceny.
    if (filters.public_price_only) q.append('public_price_only', 'true');
    if (filters.seller?.length > 0) filters.seller.forEach((s) => q.append('sellers', s));
    if (filters.price) {
      q.append('min_price', filters.price[0]);
      q.append('max_price', filters.price[1]);
    }
    if (filters.manufacturer?.length > 0) filters.manufacturer.forEach((m) => q.append('manufacturers', m));
    if (filters.types?.length > 0) filters.types.forEach((t) => q.append('types', t));
    // Seria — ma sens tylko przy JEDNYM wybranym producencie (patrz Filters.jsx),
    // ale filtr nie wymusza tego po swojej stronie: gdyby ktoś ręcznie podmienił
    // URL, backend i tak dostanie spójne żądanie, tylko wynik będzie nieoczywisty.
    if (filters.series?.length > 0) filters.series.forEach((s) => q.append('series_in', s));

    // Parametr opcjonalny liczbowy: dołącz tylko gdy faktycznie ustawiony.
    const appendIf = (name, value) => {
      if (value != null && value !== '') q.append(name, value);
    };
    // Parametry elektryczne (min_/max_ z kart katalogowych) — wspólne dla obu kategorii.
    const ELEC_PARAMS = [
      'min_vmp', 'max_vmp', 'min_imp', 'max_imp', 'min_voc', 'max_voc', 'min_isc', 'max_isc',
      'min_gamma_pmax', 'min_beta_voc',
      'min_ac_current', 'max_ac_current', 'min_mppt_current', 'max_mppt_current',
      'min_mppt_isc', 'max_mppt_isc', 'min_dc_voltage', 'max_dc_voltage',
      'min_eff_max', 'min_eff_euro',
    ];
    ELEC_PARAMS.forEach((p) => appendIf(p, filters[p]));

    if (productType === 'panel') {
      if (filters.power) {
        q.append('min_power', filters.power[0]);
        q.append('max_power', filters.power[1]);
      }
      if (filters.frame_colour) q.append('frame_colour', filters.frame_colour);
      appendIf('min_efficiency', filters.min_efficiency);
      appendIf('min_product_warranty', filters.min_product_warranty);
      appendIf('min_price_per_wp', filters.min_price_per_wp);
      appendIf('max_price_per_wp', filters.max_price_per_wp);
      // Wymiary i waga — NAPRAWA: wysyłał je tylko widok ofert, więc w domyślnym
      // widoku modeli filtr „zawsze oddawał wszystko".
      appendIf('module_length_min', filters.module_length_min);
      appendIf('module_length_max', filters.module_length_max);
      appendIf('module_width_range', filters.module_width_range);
      appendIf('module_thickness_range', filters.module_thickness_range);
      appendIf('max_weight', filters.max_weight);
      if (filters.cells_series?.length > 0) filters.cells_series.forEach((c) => q.append('cells_series', c));
    } else if (productType === 'inwerter') {
      if (filters.power) {
        q.append('min_power', filters.power[0]);
        q.append('max_power', filters.power[1]);
      }
      if (filters.phase?.length > 0) filters.phase.forEach((p) => q.append('phase', p));
      // NAPRAWA dwóch martwych filtrów: liczba MPPT (checkbox istniał, nigdy nie
      // trafiał do API) i gwarancja (backend nie znał parametru).
      if (filters.mppt?.length > 0) filters.mppt.forEach((m) => q.append('mppts', m));
      appendIf('min_warranty', filters.min_warranty);
    } else if (productType === 'magazyn') {
      if (filters.capacity) {
        q.append('min_capacity', filters.capacity[0]);
        q.append('max_capacity', filters.capacity[1]);
      }
      if (filters.voltage?.length > 0) filters.voltage.forEach((v) => q.append('voltage', v));
      MAGAZINE_ELEC_PARAMS.forEach((p) => appendIf(p, filters[p]));
      filters.ip_ratings?.forEach((v) => q.append('ip_ratings', v));
      filters.charge_temp_class?.forEach((v) => q.append('charge_temp_class', v));
    } else if (productType === 'wallbox') {
      if (filters.komercyjne?.length) q.append('komercyjne', 'true');
      filters.power_kw?.forEach((v) => q.append('power_kw', v));
      filters.phases?.forEach((v) => q.append('phases', v));
      filters.connector_types?.forEach((v) => q.append('connector_types', v));
      filters.pv_modes?.forEach((v) => q.append('pv_modes', v));
    }

    try {
      const data = await fetchProducts(`${ENDPOINT[productType]}/?${q}`);
      if (reqId !== latestReq.current) return;
      setModels(data.items || []);
      setTotalCount(data.count ?? 0);
      setTotalPages(Math.ceil((data.count || 0) / pageSize) || 1);
    } catch (err) {
      if (reqId !== latestReq.current) return;
      console.error('Error fetching product models', err);
      setTotalCount(null);
    } finally {
      if (reqId === latestReq.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    fetchModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType, pageIndex, filters, sortOrder, search, enabled]);

  return { models, totalPages, totalCount, isLoading };
};
